import React from "react";
import { Banner, Button, FormField, Input } from "@/ds";
import { cockpit } from "@/api";
import type { StaffInvitationPreview } from "@/api";
import { Wordmark } from "../brand";
import { LanguageSwitcher, useT } from "../i18n";
import { isThrottled, refusalKey, retryAfterMinutes, tokenFromHash } from "./invitation.logic";
import styles from "./screen-invitation.module.css";

/**
 * Where an invited colleague lands (#1298).
 *
 * Deliberately smaller than the business handover screen (#780): that one is somebody registering a
 * company, this one is somebody joining one that already exists. Everything about them is already
 * decided — which business, which address, which role — so the only two fields are their name and a
 * password they choose themselves. That is the entire point of the flow: nobody else ever knows
 * their first password.
 *
 * The address is shown and not editable. It is what the invitation was issued for, and the server
 * takes it from the invitation row rather than from this form, so a forwarded link cannot become an
 * account for whoever it was forwarded to. Rendering it as a field would suggest otherwise.
 *
 * The token comes from the URL fragment, read once on mount and never written back — browsers do
 * not send fragments, so the credential stays out of access logs, proxies and `Referer` headers.
 *
 * A refusal says only that the link is not usable: which of expired, withdrawn or already-used
 * applies is not something an unauthenticated caller may learn.
 */

type Phase =
  | { kind: "loading" }
  | { kind: "invalid" }
  /** The link is fine and the person must not throw it away — the opposite of "invalid" (#1163). */
  | { kind: "throttled"; minutes: number }
  | { kind: "form"; preview: StaffInvitationPreview };

export function StaffInvitationScreen() {
  const t = useT();
  const [phase, setPhase] = React.useState<Phase>({ kind: "loading" });
  const [name, setName] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [repeat, setRepeat] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const submissionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    // Keep the explanation and the retry action together in view; the remounted Banner announces
    // the error without moving focus.
    if (error) submissionRef.current?.scrollIntoView({ block: "nearest", behavior: "instant" });
  }, [error]);

  // Captured once: the fragment is the only copy there is.
  const [token] = React.useState(() => tokenFromHash(window.location.hash));

  const languageBar = (
    <>
      <div className="preauth__lang"><LanguageSwitcher light /></div>
      <div className={styles.languageBar}><Wordmark /></div>
    </>
  );

  React.useEffect(() => {
    let live = true;
    void (async () => {
      if (!token) {
        setPhase({ kind: "invalid" });
        return;
      }
      try {
        const preview = await cockpit.previewStaffInvitation(token);
        if (!live) return;
        setName(preview.invitedName ?? "");
        setPhase({ kind: "form", preview });
      } catch (e: unknown) {
        if (!live) return;
        setPhase(
          isThrottled(e) ? { kind: "throttled", minutes: retryAfterMinutes(e) } : { kind: "invalid" },
        );
      }
    })();
    return () => {
      live = false;
    };
  }, [token]);

  const mismatch = password !== "" && repeat !== "" && password !== repeat;
  const ready = password.length >= 10 && password === repeat;

  /**
   * Into the product. The session is already a cookie, and a full load drops the fragment from the
   * address bar, so the spent token does not sit in the history entry for the signed-in page.
   */
  const enter = () => window.location.replace(import.meta.env.BASE_URL || "/");

  const submit = () => {
    if (!ready) return;
    setBusy(true);
    setError(null);
    cockpit
      .acceptStaffInvitation({ token, name: name.trim() || undefined, password })
      .then(enter)
      .catch((e: unknown) => {
        setError(refusalKey(e));
        setBusy(false);
      });
  };

  if (phase.kind === "loading") {
    return (
      <div className={styles.page}>
        {languageBar}
        <p className="subtle" role="status">{t("invite.accept.loading")}</p>
      </div>
    );
  }

  if (phase.kind === "throttled") {
    return (
      <div className={styles.page} data-testid="staff-invitation-throttled">
        {languageBar}
        <div className={`page-head ${styles.header}`}>
          <h1>{t("invite.accept.contextTitle")}</h1>
        </div>
        <Banner tone="warning" title={t("invite.accept.throttledTitle")}>
          {t("invite.accept.throttledBody", { minutes: phase.minutes })}
        </Banner>
        <a className={styles.returnLink} href={`${import.meta.env.BASE_URL}login`}>
          {t("invite.accept.toLogin")}
        </a>
      </div>
    );
  }

  if (phase.kind === "invalid") {
    return (
      <div className={styles.page} data-testid="staff-invitation-invalid">
        {languageBar}
        <div className={`page-head ${styles.header}`}>
          <h1>{t("invite.accept.contextTitle")}</h1>
        </div>
        <Banner tone="danger" title={t("invite.accept.invalidTitle")}>
          {t("invite.accept.invalidBody")}
        </Banner>
        <a className={styles.returnLink} href={`${import.meta.env.BASE_URL}login`}>
          {t("invite.accept.toLogin")}
        </a>
      </div>
    );
  }

  return (
    <div className={styles.page} data-testid="staff-invitation-screen">
      {languageBar}
      <div className={`page-head ${styles.header}`}>
        <div>
          <h1>{t("staffInvite.accept.title", { business: phase.preview.businessName })}</h1>
          <p>{t("staffInvite.accept.sub")}</p>
        </div>
      </div>
      <div className={styles.form}>
        <section className={styles.section} aria-labelledby="staff-invitation-access-title">
          <h2 id="staff-invitation-access-title">{t("invite.accept.accessTitle")}</h2>
          <div className={styles.field}>
            {/* Read-only on purpose: the account can only ever be for the invited address, and the
                server reads it from the invitation rather than from anything sent here. */}
            <FormField label={t("invite.accept.emailLabel")} htmlFor="staff-invite-email">
              <Input
                id="staff-invite-email"
                readOnly
                value={phase.preview.invitedEmail}
                data-testid="staff-invitation-email"
              />
            </FormField>
            <p className="subtle" style={{ margin: 0, fontSize: "var(--text-sm)" }}>
              {t("staffInvite.accept.emailFixed")}
            </p>
          </div>
          <FormField label={t("invite.accept.nameLabel")} htmlFor="staff-invite-name">
            <Input
              id="staff-invite-name"
              autoComplete="name"
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            />
          </FormField>
          <div className={styles.field}>
            <FormField label={t("invite.accept.passwordLabel")} htmlFor="staff-invite-password">
              <Input
                id="staff-invite-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              />
            </FormField>
            <p className="subtle" style={{ margin: 0, fontSize: "var(--text-sm)" }}>
              {t("invite.accept.passwordHint")}
            </p>
          </div>
          <div className={styles.field}>
            <FormField label={t("invite.accept.passwordRepeatLabel")} htmlFor="staff-invite-repeat">
              <Input
                id="staff-invite-repeat"
                type="password"
                autoComplete="new-password"
                value={repeat}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRepeat(e.target.value)}
              />
            </FormField>
            {mismatch && (
              <p style={{ margin: 0, fontSize: "var(--text-sm)" }}>
                {t("invite.accept.passwordMismatch")}
              </p>
            )}
          </div>
        </section>

        <div ref={submissionRef} className={styles.submission}>
          {error && <Banner tone="danger" title={t("common.error")}>{t(error)}</Banner>}
          <div>
            <Button loading={busy} variant="primary" disabled={busy || !ready} aria-busy={busy} onClick={submit}>
              {t("staffInvite.accept.submit")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
